// Dynamically import @honeybadger-io/js so the ~5.6 MB SDK is split out of
// the initial chunk. Errors raised before the SDK finishes loading are
// queued and flushed once it's ready.

let honeybadger = null;
let loading = null;
const queue = [];

function load() {
  if (loading) return loading;

  loading = import('@honeybadger-io/js').then(({ default: Honeybadger }) => {
    Honeybadger.configure({
      apiKey: process.env.HONEYBADGER_API_KEY,
      environment: process.env.RAILS_ENV || process.env.NODE_ENV,
      revision: process.env.HEROKU_SLUG_COMMIT,
    });

    // Drop known third-party / scanner / network noise that isn't
    // actionable in our code and is extremely high volume:
    //  - NS_ERROR_NOT_INITIALIZED: Firefox-internal error from inside
    //    Intercom's messenger frame when browser storage is unavailable.
    //  - "Object Not Found Matching Id...": Microsoft Outlook SafeLinks
    //    scanner artifact, fired when emailed links are crawled.
    //  - "Load failed" / "NetworkError..." / "Failed to fetch": an aborted
    //    or failed fetch() (navigation away, dropped connection, offline,
    //    ad blocker). Same thing across Safari / Firefox / Chrome.
    //  - "Unspecified reason" / "{}": a promise rejected with no Error /
    //    empty object (often Safari extensions); no payload to act on.
    const IGNORED_MESSAGES = [
      'NS_ERROR_NOT_INITIALIZED',
      'Object Not Found Matching Id',
      'Load failed',
      'NetworkError when attempting to fetch',
      'Failed to fetch',
      'Unspecified reason',
    ];
    Honeybadger.beforeNotify((notice) => {
      if (!notice) return;
      if (notice.name === 'NS_ERROR_NOT_INITIALIZED') return false;
      if (typeof notice.message === 'string') {
        const message = notice.message;
        if (message.trim() === '{}') return false;
        if (IGNORED_MESSAGES.some((m) => message.indexOf(m) !== -1)) {
          return false;
        }
      }
    });

    honeybadger = Honeybadger;

    queue.forEach(({ err, context }) => Honeybadger.notify(err, { context }));
    queue.length = 0;

    return Honeybadger;
  }).catch((loadErr) => {
    // Don't break the host app if the chunk fails to fetch.
    // eslint-disable-next-line no-console
    console.error('Failed to load Honeybadger SDK:', loadErr);
  });

  return loading;
}

function notify(err, context = {}) {
  if (honeybadger) {
    honeybadger.notify(err, { context });
  } else {
    queue.push({ err, context });
    load();
  }
}

window.HoneyBadger = {
  report(payload) {
    if (payload instanceof Error) {
      notify(payload);
      return;
    }
    const { err, ...context } = payload || {};
    notify(err || 'Manual HoneyBadger.report', context);
  },
};

// Catch uncaught errors during the SDK-loading window. Once the SDK is
// loaded, Honeybadger.configure() installs its own window.onerror handler;
// our listener becomes a no-op via the honeybadger guard.
window.addEventListener('error', (event) => {
  if (honeybadger) return;
  notify(event.error || new Error(event.message || 'window.onerror'), {
    source: 'window.onerror',
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
});

window.addEventListener('unhandledrejection', (event) => {
  if (honeybadger) return;
  const reason = event.reason;
  const err = reason instanceof Error ? reason : new Error(String(reason));
  notify(err, { source: 'unhandledrejection' });
});

// Preload the SDK during idle time so it's ready for the common case
// without blocking initial render. Falls back to a short timeout where
// requestIdleCallback isn't supported.
if (typeof window.requestIdleCallback === 'function') {
  window.requestIdleCallback(load, { timeout: 2000 });
} else {
  setTimeout(load, 100);
}

export default window.HoneyBadger;
